import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrdenesPermisos } from '../../hooks/useOrdenesPermisos';
import { ordenesDispositivosAPI, ordenesClientesAPI } from '../../lib/ordenesApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Checkbox } from '../../components/ui/checkbox';
import { ColumnSelector } from '../../components/ui/column-selector';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
    Cpu,
    Search,
    ArrowLeft,
    Trash2,
    Loader2,
    Car,
    Building2,
    RefreshCw,
} from 'lucide-react';

const DEVICE_COLUMNS = [
    { key: 'imei', label: 'IMEI', required: true, defaultVisible: true },
    { key: 'patente', label: 'Patente', defaultVisible: true },
    { key: 'cliente', label: 'Razón Social', defaultVisible: true },
    { key: 'rut', label: 'RUT Cliente', defaultVisible: true },
    { key: 'tipo', label: 'Tipo', defaultVisible: false },
    { key: 'marca_modelo', label: 'Marca/Modelo', defaultVisible: false },
];

export default function DispositivosInstaladosPage() {
    const navigate = useNavigate();
    const { tienePermiso, loading: permisosLoading } = useOrdenesPermisos();
    const [loading, setLoading] = useState(true);
    const [dispositivos, setDispositivos] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [search, setSearch] = useState('');
    const [clienteFilter, setClienteFilter] = useState('');
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [saving, setSaving] = useState(false);
    
    // Selección múltiple
    const [selectedIds, setSelectedIds] = useState([]);
    
    // Columnas visibles
    const [visibleColumns, setVisibleColumns] = useState(
        DEVICE_COLUMNS.filter(c => c.defaultVisible !== false).map(c => c.key)
    );

    useEffect(() => {
        loadInitialData();
    }, []);

    useEffect(() => {
        loadDispositivos();
    }, [clienteFilter]);

    const loadInitialData = async () => {
        try {
            const clientesRes = await ordenesClientesAPI.getAll();
            setClientes(clientesRes.data);
        } catch (err) {
            console.error('Error loading clientes:', err);
        }
    };

    const loadDispositivos = async () => {
        try {
            setLoading(true);
            const params = {};
            if (clienteFilter && clienteFilter !== '_all') params.cliente_id = clienteFilter;
            const res = await ordenesDispositivosAPI.getAll(params);
            setDispositivos(res.data);
            setSelectedIds([]);
        } catch (err) {
            console.error('Error loading dispositivos:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDelete = (item) => {
        setEditingItem(item);
        setShowDeleteDialog(true);
    };

    const handleDelete = async () => {
        setSaving(true);
        try {
            await ordenesDispositivosAPI.delete(editingItem.id);
            toast.success('Dispositivo desinstalado');
            setShowDeleteDialog(false);
            loadDispositivos();
        } catch (err) {
            toast.error('Error al desinstalar dispositivo');
        } finally {
            setSaving(false);
        }
    };

    // Selección múltiple
    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedIds(filteredDispositivos.map(d => d.id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (id, checked) => {
        if (checked) {
            setSelectedIds([...selectedIds, id]);
        } else {
            setSelectedIds(selectedIds.filter(i => i !== id));
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        
        setSaving(true);
        try {
            const res = await ordenesDispositivosAPI.deleteBulk(selectedIds);
            toast.success(res.data.message);
            setShowBulkDeleteDialog(false);
            setSelectedIds([]);
            loadDispositivos();
        } catch (err) {
            toast.error('Error al desinstalar dispositivos');
        } finally {
            setSaving(false);
        }
    };

    const filteredDispositivos = dispositivos.filter(d => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        return (
            d.serial?.toLowerCase().includes(searchLower) ||
            d.imei?.toLowerCase().includes(searchLower) ||
            d.vehiculo?.patente?.toLowerCase().includes(searchLower) ||
            d.cliente_razon_social?.toLowerCase().includes(searchLower)
        );
    });

    const allSelected = filteredDispositivos.length > 0 && selectedIds.length === filteredDispositivos.length;
    const someSelected = selectedIds.length > 0 && selectedIds.length < filteredDispositivos.length;

    if (permisosLoading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6 p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/ordenes/dashboard')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Cpu className="h-6 w-6" />
                            Dispositivos Instalados
                        </h1>
                        <p className="text-muted-foreground">Gestión de dispositivos GPS instalados en vehículos</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={loadDispositivos} data-testid="refresh-btn">
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Actualizar
                    </Button>
                    {selectedIds.length > 0 && tienePermiso('gestionar_inventario') && (
                        <Button 
                            variant="destructive" 
                            onClick={() => setShowBulkDeleteDialog(true)}
                            data-testid="bulk-delete-btn"
                        >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Desinstalar ({selectedIds.length})
                        </Button>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4 items-center">
                <div className="relative flex-1 min-w-[200px] max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Buscar por IMEI, patente, cliente..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10"
                        data-testid="search-input"
                    />
                </div>
                
                <Select value={clienteFilter || "_all"} onValueChange={(v) => setClienteFilter(v === "_all" ? "" : v)}>
                    <SelectTrigger className="w-[200px]" data-testid="cliente-filter">
                        <SelectValue placeholder="Filtrar por cliente" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="_all">Todos los clientes</SelectItem>
                        {clientes.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.razon_social}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                
                <ColumnSelector
                    columns={DEVICE_COLUMNS}
                    visibleColumns={visibleColumns}
                    onVisibilityChange={setVisibleColumns}
                    storageKey="dispositivos-instalados-columns"
                />
            </div>

            {/* Table */}
            <Card className="border-2">
                <CardContent className="p-0 overflow-x-auto">
                    {loading ? (
                        <div className="p-8">
                            <Skeleton className="h-64 w-full" />
                        </div>
                    ) : filteredDispositivos.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <Cpu className="h-12 w-12 mx-auto mb-3 opacity-50" />
                            <p>No hay dispositivos instalados</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-12">
                                        <Checkbox 
                                            checked={allSelected}
                                            ref={(el) => el && (el.indeterminate = someSelected)}
                                            onCheckedChange={handleSelectAll}
                                            data-testid="select-all-checkbox"
                                        />
                                    </TableHead>
                                    {visibleColumns.includes('imei') && <TableHead>IMEI</TableHead>}
                                    {visibleColumns.includes('patente') && <TableHead>Patente</TableHead>}
                                    {visibleColumns.includes('cliente') && <TableHead>Razón Social</TableHead>}
                                    {visibleColumns.includes('rut') && <TableHead>RUT Cliente</TableHead>}
                                    {visibleColumns.includes('tipo') && <TableHead>Tipo</TableHead>}
                                    {visibleColumns.includes('marca_modelo') && <TableHead>Marca/Modelo</TableHead>}
                                    <TableHead className="text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredDispositivos.map((item) => (
                                    <TableRow key={item.id} data-testid={`device-${item.id}`}>
                                        <TableCell>
                                            <Checkbox 
                                                checked={selectedIds.includes(item.id)}
                                                onCheckedChange={(checked) => handleSelectOne(item.id, checked)}
                                            />
                                        </TableCell>
                                        {visibleColumns.includes('imei') && (
                                            <TableCell className="font-mono font-bold">
                                                {item.imei || item.serial || '-'}
                                            </TableCell>
                                        )}
                                        {visibleColumns.includes('patente') && (
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <Car className="h-4 w-4 text-muted-foreground" />
                                                    <span className="font-medium">{item.vehiculo?.patente || item.patente || '-'}</span>
                                                </div>
                                            </TableCell>
                                        )}
                                        {visibleColumns.includes('cliente') && (
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <Building2 className="h-4 w-4 text-muted-foreground" />
                                                    <span>{item.cliente_razon_social || '-'}</span>
                                                </div>
                                            </TableCell>
                                        )}
                                        {visibleColumns.includes('rut') && (
                                            <TableCell className="font-mono text-sm">
                                                {item.cliente_rut || '-'}
                                            </TableCell>
                                        )}
                                        {visibleColumns.includes('tipo') && (
                                            <TableCell>
                                                <Badge variant="outline">{item.tipo_dispositivo || 'GPS'}</Badge>
                                            </TableCell>
                                        )}
                                        {visibleColumns.includes('marca_modelo') && (
                                            <TableCell>
                                                {item.marca && item.modelo ? `${item.marca} ${item.modelo}` : item.marca || item.modelo || '-'}
                                            </TableCell>
                                        )}
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-1">
                                                {tienePermiso('gestionar_inventario') && (
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm" 
                                                        onClick={() => handleOpenDelete(item)}
                                                        title="Desinstalar"
                                                        data-testid={`delete-${item.id}`}
                                                    >
                                                        <Trash2 className="h-4 w-4 text-red-500" />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Summary */}
            <div className="text-sm text-muted-foreground text-center">
                {selectedIds.length > 0 ? (
                    <span className="text-primary font-medium">{selectedIds.length} seleccionados de </span>
                ) : null}
                {filteredDispositivos.length} dispositivos instalados
            </div>

            {/* Delete Confirmation */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Desinstalar Dispositivo</DialogTitle>
                        <DialogDescription>
                            ¿Estás seguro de desinstalar el dispositivo <strong>{editingItem?.imei || editingItem?.serial}</strong>? 
                            El dispositivo quedará marcado como inactivo.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancelar</Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={saving}>
                            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Desinstalar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk Delete Confirmation */}
            <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Desinstalar Dispositivos</DialogTitle>
                        <DialogDescription>
                            ¿Estás seguro de desinstalar <strong>{selectedIds.length} dispositivos</strong>? 
                            Los dispositivos quedarán marcados como inactivos.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowBulkDeleteDialog(false)}>Cancelar</Button>
                        <Button variant="destructive" onClick={handleBulkDelete} disabled={saving}>
                            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Desinstalar {selectedIds.length} dispositivos
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
